<script setup lang="ts">
import { computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useDisplay } from 'vuetify'
import {
  mdiAlertOutline,
  mdiClose,
  mdiCloudUploadOutline,
  mdiDownloadOutline,
} from '@mdi/js'
import { useUploadStore } from '../../stores/useUploadStore'
import { useExportJobsStore } from '../../stores/useExportJobsStore'
import { useUploadFlow } from '../../composables/useUploadFlow'
import UploadDropZone from './UploadDropZone.vue'
import UploadItem from './UploadItem.vue'
import ExportItem from './ExportItem.vue'
import UploadProgressHeader from './UploadProgressHeader.vue'
import { useDrawerDismiss } from '@/apps/core/composables/useDrawerDismiss'

interface Props {
  width?: string | number
  location?: 'left' | 'right'
}

withDefaults(defineProps<Props>(), {
  width: 400,
  location: 'right',
})

const store = useUploadStore()
const exportJobs = useExportJobsStore()
const { uploadFiles } = useUploadFlow()
const { t } = useI18n()
// A phone opens it from Plus's sheet, and is narrower than its width: it takes the screen.
const { mobile, width: screenWidth } = useDisplay()

// Escape and back close it, as they close a sheet.
useDrawerDismiss(() => store.drawerOpen, () => store.closeDrawer())

const uploadCount = computed(() => store.uploadList.length)

const exportCount = computed(() => exportJobs.jobs.length)

const hasInactiveUploads = computed(
  () =>
    store.uploadList.some(
      (u) => u.status === 'completed' || u.status === 'failed' || u.status === 'cancelled',
    ),
)

// Auto-switch to the exports tab when one starts and the user hasn't been
// looking at uploads recently — only when no upload is currently active.
watch(
  () => exportJobs.activeCount,
  (count, prev) => {
    if (count > (prev ?? 0) && uploadCount.value === 0) {
      store.drawerTab = 'exports'
    }
  },
)

// The dock's "Later" has no place on a phone, where Plus's badge stands in for the dock: closing
// the drawer after the exports tab was shown is the same acknowledgement. The exports stay listed.
let exportsSeen = false
watch(
  () => [store.drawerOpen, store.drawerTab] as const,
  ([open, tab]) => {
    if (open && tab === 'exports' && mobile.value) exportsSeen = true
    if (!open && exportsSeen) {
      exportsSeen = false
      exportJobs.acknowledgeAll()
    }
  },
)

function handleFilesSelected(files: File[]) {
  void uploadFiles(files)
}

function handleError(message: string) {
  console.error('Upload error:', message)
}
</script>

<template>
  <!-- `touchless`: Vuetify opens a temporary drawer on a swipe from its screen edge, and on a
       phone a swipe from the right is also the browser's back gesture or a carousel's — it opened
       the transfers by accident. They open from the rail, and on a phone from Plus's sheet. -->
  <v-navigation-drawer
    v-model="store.drawerOpen"
    :width="mobile ? screenWidth : width"
    :location="location"
    temporary
    touchless
    class="upload-drawer"
  >
    <!-- Header -->
    <div class="upload-drawer__header">
      <h3 class="upload-drawer__title">
        {{ t('assets.transfers.drawer.title') }}
      </h3>

      <v-btn
        icon
        variant="text"
        size="small"
        @click="store.closeDrawer"
      >
        <v-icon :icon="mdiClose" size="20" />
      </v-btn>
    </div>

    <!-- Tabs -->
    <v-tabs
      v-model="store.drawerTab"
      density="compact"
      color="primary"
      grow
      class="upload-drawer__tabs"
    >
      <v-tab value="uploads" class="upload-drawer__tab">
        <v-icon :icon="mdiCloudUploadOutline" size="18" start />
        {{ t('assets.transfers.tabs.uploads') }}
        <span v-if="uploadCount > 0" class="upload-drawer__tab-count">
          {{ uploadCount }}
        </span>
      </v-tab>
      <v-tab value="exports" class="upload-drawer__tab">
        <v-icon :icon="mdiDownloadOutline" size="18" start />
        {{ t('assets.transfers.tabs.exports') }}
        <span v-if="exportCount > 0" class="upload-drawer__tab-count">
          {{ exportCount }}
        </span>
      </v-tab>
    </v-tabs>

    <!-- Uploads tab -->
    <div v-if="store.drawerTab === 'uploads'" class="upload-drawer__panel">
      <div class="upload-drawer__dropzone">
        <UploadDropZone disable-drag @files-selected="handleFilesSelected" @error="handleError" />
      </div>

      <UploadProgressHeader />

      <Transition name="fade-slide">
        <v-alert
          v-if="store.hasActiveOrPendingUploads"
          type="warning"
          variant="tonal"
          density="compact"
          class="upload-drawer__warning"
          :icon="mdiAlertOutline"
        >
          {{ t('assets.upload.drawer.keepTabOpenWarning') }}
        </v-alert>
      </Transition>

      <div
        v-if="store.hasActiveOrPendingUploads || hasInactiveUploads"
        class="upload-drawer__clear-bar"
      >
        <button
          v-if="store.hasActiveOrPendingUploads"
          class="upload-drawer__clear-btn upload-drawer__clear-btn--error"
          @click="store.cancelActive"
        >
          {{ t('assets.upload.drawer.cancelAllActive') }}
        </button>
        <button
          v-if="hasInactiveUploads"
          class="upload-drawer__clear-btn upload-drawer__clear-btn--neutral"
          @click="store.clearInactive"
        >
          {{ t('assets.upload.drawer.clearInactive') }}
        </button>
      </div>

      <div class="upload-drawer__list">
        <template v-if="uploadCount > 0">
          <TransitionGroup name="upload-list" tag="div" class="upload-drawer__list-inner">
            <UploadItem
              v-for="upload in store.uploadList"
              :key="upload.id"
              :upload="upload"
              @pause="store.pauseUpload"
              @resume="store.resumeUpload"
              @cancel="store.cancelUpload"
              @retry="store.retryUpload"
              @remove="store.removeUpload"
            />
          </TransitionGroup>
        </template>

        <div v-else class="upload-drawer__empty">
          <div class="upload-drawer__empty-icon">
            <v-icon :icon="mdiCloudUploadOutline" size="48" />
          </div>
          <p class="upload-drawer__empty-title">
            {{ t('assets.upload.drawer.empty') }}
          </p>
          <p class="upload-drawer__empty-hint">
            {{ t('assets.upload.drawer.emptyHelper') }}
          </p>
        </div>
      </div>
    </div>

    <!-- Exports tab -->
    <div v-else class="upload-drawer__panel">
      <div v-if="exportJobs.hasReady || exportJobs.hasFailed" class="upload-drawer__clear-bar">
        <button
          v-if="exportJobs.hasReady"
          class="upload-drawer__clear-btn upload-drawer__clear-btn--success"
          @click="exportJobs.clearReady"
        >
          {{ t('assets.upload.drawer.clearCompleted') }}
        </button>
        <button
          v-if="exportJobs.hasFailed"
          class="upload-drawer__clear-btn upload-drawer__clear-btn--error"
          @click="exportJobs.clearFailed"
        >
          {{ t('assets.upload.drawer.clearFailed') }}
        </button>
      </div>

      <div class="upload-drawer__list">
        <template v-if="exportCount > 0">
          <TransitionGroup name="upload-list" tag="div" class="upload-drawer__list-inner">
            <ExportItem
              v-for="job in exportJobs.jobs"
              :key="job.id"
              :job="job"
              @remove="exportJobs.remove"
              @download="exportJobs.download"
              @retry="exportJobs.retry"
            />
          </TransitionGroup>
        </template>

        <div v-else class="upload-drawer__empty">
          <div class="upload-drawer__empty-icon">
            <v-icon :icon="mdiDownloadOutline" size="48" />
          </div>
          <p class="upload-drawer__empty-title">
            {{ t('assets.transfers.exports.empty') }}
          </p>
          <p class="upload-drawer__empty-hint">
            {{ t('assets.transfers.exports.emptyHelper') }}
          </p>
        </div>
      </div>
    </div>
  </v-navigation-drawer>
</template>

<style scoped lang="scss">
.upload-drawer {
  display: flex;
  flex-direction: column;

  // On a phone the drawer stops at the bottom bar, and the mini player's bar sits on its last
  // 46 px; 0 everywhere else (docs/plans/mobile-navigation.md, MN4).
  :deep(.v-navigation-drawer__content) {
    padding-bottom: var(--app-mini-player-h);
  }

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 8px 12px 16px;
    border-bottom: 1px solid rgba(var(--v-border-color), 0.08);
  }

  &__title {
    font-size: 16px;
    font-weight: 600;
    letter-spacing: -0.01em;
  }

  &__tabs {
    border-bottom: 1px solid rgba(var(--v-border-color), 0.08);
  }

  &__tab {
    text-transform: none;
    font-size: 13px;
    font-weight: 500;
    letter-spacing: 0;
    min-width: 0;
  }

  &__tab-count {
    font-size: 11px;
    font-weight: 500;
    padding: 1px 7px;
    margin-left: 6px;
    border-radius: 10px;
    background: rgba(var(--v-theme-primary), 0.12);
    color: rgb(var(--v-theme-primary));
  }

  &__panel {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
  }

  &__dropzone {
    padding: 12px;
  }

  &__warning {
    margin: 0 12px 8px;
    font-size: 12px;
    line-height: 1.4;
  }

  &__clear-bar {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 12px;
    border-bottom: 1px solid rgba(var(--v-border-color), 0.06);
  }

  &__clear-btn {
    font-size: 12px;
    font-weight: 500;
    padding: 4px 10px;
    border-radius: 6px;
    border: none;
    cursor: pointer;
    transition: all 0.15s ease;

    &--success {
      background: rgba(var(--v-theme-success), 0.1);
      color: rgb(var(--v-theme-success));

      &:hover {
        background: rgba(var(--v-theme-success), 0.18);
      }
    }

    &--error {
      background: rgba(var(--v-theme-error), 0.1);
      color: rgb(var(--v-theme-error));

      &:hover {
        background: rgba(var(--v-theme-error), 0.18);
      }
    }

    &--neutral {
      background: rgba(var(--v-theme-on-surface), 0.06);
      color: rgba(var(--v-theme-on-surface), 0.75);

      &:hover {
        background: rgba(var(--v-theme-on-surface), 0.1);
      }
    }
  }

  &__list {
    flex: 1;
    overflow-y: auto;
  }

  &__list-inner {
    position: relative;
  }

  &__empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 48px 24px;
    text-align: center;
  }

  &__empty-icon {
    width: 80px;
    height: 80px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 20px;
    background: rgba(var(--v-theme-primary), 0.06);
    color: rgba(var(--v-theme-primary), 0.4);
    margin-bottom: 16px;
  }

  &__empty-title {
    font-size: 15px;
    font-weight: 500;
    color: rgba(var(--v-theme-on-surface), 0.75);
    margin: 0 0 4px;
  }

  &__empty-hint {
    font-size: 13px;
    color: rgba(var(--v-theme-on-surface), 0.45);
    margin: 0;
  }
}

.upload-list-enter-active {
  transition: all 0.35s cubic-bezier(0.4, 0, 0.2, 1);
}

.upload-list-leave-active {
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  position: absolute;
  width: 100%;
}

.upload-list-enter-from {
  opacity: 0;
  transform: translateX(20px);
}

.upload-list-leave-to {
  opacity: 0;
  transform: translateX(-20px);
}

.upload-list-move {
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.fade-slide-enter-from,
.fade-slide-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}
</style>
